import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { Clear } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { masterAPI } from "../../Masters/dataConfig";
import { axiosPrivate } from "../../../axios/axios";
import { color } from "../../../StyleData/Config";
import { getCurrentInTimeZone, getDateWithOutUTC, toUTC } from "../../../utils/dateUtil";
import NormalTable from "../../../Component/NormalTable";
import { woStatus } from "../../../ConfigInfo/Info";

const ReturnTransModal = ({ handleCloseClick }) => {
  const [issueList, setIssueList] = useState([]);
  const [issueTypeData, setIssueTypeData] = useState([]);
  const [returnType, setReturnType] = useState("wo");
  const [partData, setPartData] = useState([]);
  const [personData, setPersonData] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [issueChangeOpen, setIssueChangeOpen] = useState(false);
  const [pendingIssueChange, setPendingIssueChange] = useState(null);
  const [loading, setLoading] = useState(false);

  const initialValue = {
    workOrderId: null,
    returnedDate: getCurrentInTimeZone().dayjsObject,
    returnedById: null,
  };

  const isClosedOrCancelled = (item) =>
    [item.status, item.workOrderStatus, item.workOrder?.status]
      .filter(Boolean)
      .some((status) => [woStatus.CLOSE, woStatus.CAN].includes(String(status).toUpperCase()));

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const workOrders = await axiosPrivate.get(masterAPI.workOrder.link);
        const people = await axiosPrivate.get(masterAPI.person.link);
        const result = workOrders.data.result || [];
        setIssueList(result);
        setIssueTypeData(result.filter((item) => item.id && !isClosedOrCancelled(item)));
        setPersonData(people.data.result || []);
      } catch (error) {
        setIssueTypeData([]);
        setPersonData([]);
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  const filterOptions = createFilterOptions();
  const customPaper = (props) => <Paper elevation={8} {...props} />;

  const handleReturnType = (event) => {
    const nextType = event.target.value;
    setReturnType(nextType);
    setPartData([]);
    setIssueTypeData(issueList.filter((item) => item.id && !isClosedOrCancelled(item)));
  };

  const loadIssue = async (value, setFieldValue) => {
    setFieldValue("workOrderId", value ? value.id : null);
    if (!value) {
      setPartData([]);
      return;
    }
    setPartData([]);
    try {
      const response = await axiosPrivate.get(`${masterAPI.issue.getIssuesAgainstWorkOrder}/${value.id}`);
      if (response.status === 204 || response.data?.statusCode === 204) {
        toast.error(response.data?.failureMessage || "No issues found for the given Work Order.");
        return;
      }
      const issuedItems = (response.data.result || []).flatMap((issue) =>
        Array.isArray(issue.issuedItems)
          ? issue.issuedItems.map((item) => ({ ...issue, ...item }))
          : [issue],
      );
      setPartData(issuedItems.map((item) => ({ ...item, returnQuantity: "" })));
    } catch (error) {
      setPartData([]);
      console.log(error);
    }
  };

  const handleIssueChange = (value, setFieldValue) => {
    const hasReturnQuantities = partData.some((item) => Number(item.returnQuantity) > 0);
    if (hasReturnQuantities) {
      setPendingIssueChange({ value, setFieldValue });
      setIssueChangeOpen(true);
      return;
    }
    loadIssue(value, setFieldValue);
  };

  const confirmIssueChange = () => {
    if (pendingIssueChange) {
      loadIssue(pendingIssueChange.value, pendingIssueChange.setFieldValue);
    }
    setPendingIssueChange(null);
    setIssueChangeOpen(false);
  };

  const handleReturnQuantityChange = useCallback((id, value) => {
    setPartData((items) => items.map((item) => item.id === id ? { ...item, returnQuantity: value } : item));
  }, []);

  const getSelectedItems = (value) => {
    const invalid = partData.find((item) => {
      const quantity = item.returnQuantity;
      return quantity !== "" && (!/^\d+$/.test(String(quantity)) || Number(quantity) <= 0 || Number(quantity) > Number(item.quantity));
    });
    if (invalid) {
      toast.warn("Return Quantity must be a positive whole number and cannot exceed Issued Quantity");
      return null;
    }
    const selected = partData.filter((item) => item.returnQuantity !== "" && Number(item.returnQuantity) > 0).map((item) => {
      const quantity = Number(item.returnQuantity);
      const unitCost = Number(item.unitCost) || 0;
      return {
        id: null,
        issueId: item.issueId,
        remarks: item.remarks || "",
        partId: item.partId,
        stockRoomId: item.stockRoomId,
        stockRoomRowId: item.stockRoomRowId || null,
        stockRoomRowAisleId: item.stockRoomRowAisleId || null,
        stockRoomRowBinId: item.stockRoomRowBinId || null,
        issueItemId: item.id,
        quantity,
        unitCost,
        lineCost: quantity * unitCost,
      };
    });
    if (selected.length === 0) {
      toast.warn("At least one part must have Return Quantity greater than 0");
      return null;
    }
    return selected;
  };

  const onSubmit = (value) => {
    if (!value.returnedById) {
      toast.warn("Returned By is required");
      return;
    }
    const selectedItems = getSelectedItems(value);
    if (selectedItems) {
      setPendingSubmit({ value, selectedItems });
      setConfirmOpen(true);
    }
  };

  const confirmReturn = async () => {
    if (!pendingSubmit) return;
    setLoading(true);
    try {
      await axiosPrivate.post(masterAPI.issueReturn.create, {
        ...pendingSubmit.value,
        returnedDate: toUTC(pendingSubmit.value.returnedDate).utcISOString,
        issuedRetunItemVMList: pendingSubmit.selectedItems,
      });
      setConfirmOpen(false);
      handleCloseClick();
    } catch (error) {
      console.log(error, "create IssueReturn");
    } finally {
      setLoading(false);
    }
  };

  const partsToReturn = partData.filter((item) => Number(item.returnQuantity) > 0).length;
  const totalReturnQuantity = partData.reduce((total, item) => total + (Number(item.returnQuantity) || 0), 0);
  const partColumns = useMemo(() => [
    { Header: "No.", accessor: "id", disableFilters: true, Cell: ({ row }) => row.index + 1 },
    { Header: "Part", accessor: "partCode", Cell: ({ cell }) => cell.value || cell.row.original.code },
    { Header: "Description", accessor: "partName", disableFilters: true },
    { Header: "Stock Room", accessor: "stockRoomName" },
    { Header: "Aisle", accessor: "aisleName", disableFilters: true },
    { Header: "Row", accessor: "rowName", disableFilters: true },
    { Header: "Bin", accessor: "binName", disableFilters: true },
    { Header: "Issued Quantity", accessor: "quantity", disableFilters: true },
    {
      Header: "Return Quantity",
      accessor: "returnQuantity",
      disableFilters: true,
      Cell: ({ cell }) => {
        const item = cell.row.original;
        const invalid = item.returnQuantity !== "" && (!/^\d+$/.test(String(item.returnQuantity)) || Number(item.returnQuantity) <= 0 || Number(item.returnQuantity) > Number(item.quantity));
        return (
          <TextField
            size="small"
            type="number"
            sx={{ width: 120 }}
            value={item.returnQuantity}
            onChange={(event) => handleReturnQuantityChange(item.id, event.target.value)}
            inputProps={{ min: 1, step: 1, max: item.quantity }}
            error={invalid}
            helperText={item.returnQuantity !== "" && Number(item.returnQuantity) > Number(item.quantity) ? `Max return quantity: ${item.quantity}` : item.returnQuantity !== "" && (!/^\d+$/.test(String(item.returnQuantity)) || Number(item.returnQuantity) <= 0) ? "Enter a positive whole number" : " "}
          />
        );
      },
    },
  ], [handleReturnQuantityChange]);

  return (
    <Box sx={{ p: color.masterPageDialogPadding }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <Typography sx={{ color: color.masterDialogTitleColor, fontSize: color.masterDialogFontSize }}>New Return Issue</Typography>
        <IconButton onClick={handleCloseClick}><Clear /></IconButton>
      </Box>
      <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />
      <Formik initialValues={initialValue} onSubmit={onSubmit}>
        {({ setFieldValue, values }) => (
          <Form>
            <Grid container spacing="1rem">
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>Return Type</Typography>
                <Select fullWidth size="small" value={returnType} onChange={handleReturnType}>
                  <MenuItem value="asset">Asset</MenuItem>
                  <MenuItem value="wo">Work Order</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>{returnType === "wo" ? "Work Order" : "Asset"}</Typography>
                <Autocomplete value={issueTypeData.find((item) => item.id === values.workOrderId) || null} options={issueTypeData} filterOptions={filterOptions} PaperComponent={customPaper} loading={loading} getOptionLabel={(option) => `${option.code || option.workOrderCode || ""} - ${option.workOrderName || ""}`} onChange={(event, value) => handleIssueChange(value, setFieldValue)} renderInput={(params) => <TextField {...params} placeholder="Select Work Order" size="small" />} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>Returned By</Typography>
                <Autocomplete value={personData.find((person) => person.id === values.returnedById) || null} options={personData} filterOptions={filterOptions} PaperComponent={customPaper} getOptionLabel={(option) => option.personName || ""} onChange={(event, value) => setFieldValue("returnedById", value ? value.id : null)} renderInput={(params) => <TextField {...params} placeholder="Select Person" size="small" />} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>Transaction Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker format="DD/MM/YYYY hh:mm:ss A" value={values.returnedDate} onChange={(date) => date && setFieldValue("returnedDate", getDateWithOutUTC(date))} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", p: "0.5rem 0" }}>
                  <Typography sx={{ color: color.masterDialogTitleColor, fontSize: color.masterDialogFontSize }}>Issued Items</Typography>
                  <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, gap: { xs: "1rem", sm: "2rem" }, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>Parts to Return: {partsToReturn}</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>Total Return Quantity: {totalReturnQuantity}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} />
                <NormalTable data={partData} columns={partColumns} filt={true} />
              </Grid>
              <Grid container item xs={12} justifyContent="flex-end" gap="1rem">
                <Button onClick={handleCloseClick} variant="contained" sx={{ backgroundColor: color.TableCancelButton, textTransform: "none", color: color.CancelTextColor }}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={loading} sx={{ textTransform: "none", backgroundColor: color.TableButtonColor }}>Submit</Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Return</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to return the selected parts?</DialogContentText><Typography sx={{ mt: 2 }}>Parts to Return: {pendingSubmit?.selectedItems.length || 0}</Typography><Typography>Total Return Quantity: {pendingSubmit?.selectedItems.reduce((total, item) => total + item.quantity, 0) || 0}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setConfirmOpen(false)}>Cancel</Button><Button onClick={confirmReturn} variant="contained" disabled={loading} sx={{ backgroundColor: color.TableButtonColor, textTransform: "none" }}>Confirm Return</Button></DialogActions>
      </Dialog>
      <Dialog open={issueChangeOpen} onClose={() => setIssueChangeOpen(false)}>
        <DialogTitle>Change Work Order?</DialogTitle>
        <DialogContent>
          <DialogContentText>Changing the Work Order will clear the entered Return Quantities. Do you want to continue?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueChangeOpen(false)}>Cancel</Button>
          <Button onClick={confirmIssueChange} variant="contained" sx={{ backgroundColor: color.TableButtonColor, textTransform: "none" }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReturnTransModal;